import { useRef, useEffect, useState } from 'react';

import styled from 'styled-components';

import Glide from '@glidejs/glide';
import '@glidejs/glide/dist/css/glide.core.css';

import { Link } from 'react-router-dom';

import { Button, IconButton } from '@mui/material';
import { ArrowForward, ArrowBack } from '@mui/icons-material';
import { Text } from '../../modules/acropolisCommon/components/Texts';

const Slider = ({
    array,
    card: Card,
    to,
    config,
    element = 'glide',
    containerHeight = '430px',
    itemHeight = '400px',
    title = '',
    limit
}) => {
    const sliderRef = useRef(null);
    const [isAtStart, setIsAtStart] = useState(true);
    const [isAtEnd, setIsAtEnd] = useState(false);
    const limitedArray = limit ? array.slice(0, limit) : array;

    const getBreakpoints = () => {
        const itemCount = limitedArray.length;

        if (itemCount === 1) {
            return {
                860: { perView: 1 },
                1200: { perView: 1 },
                1600: { perView: 1 },
                2200: { perView: 1 },
            };
        } else if (itemCount === 2) {
            return {
                860: { perView: 1 },
                1200: { perView: 2 },
                1600: { perView: 2 },
                2200: { perView: 2 },
            };
        } else if (itemCount === 3) {
            return {
                860: { perView: 1 },
                1200: { perView: 2 },
                1600: { perView: 3 },
                2200: { perView: 3 },
            };
        } else {
            return {
                860: { perView: 1 },
                1200: { perView: 2 },
                1600: { perView: 3 },
                2200: { perView: 4 },
            };
        }
    };

    const sliderConfig = config || {
        type: 'slider',
        startAt: 0,
        perView: Math.min(4, limitedArray.length),
        gap: 28,
        rewind: false,
        bound: true,
        breakpoints: getBreakpoints(),
    };

    useEffect(() => {
        sliderRef.current = new Glide(`.${element}`, sliderConfig);

        sliderRef.current.on(['mount.after', 'run'], () => {
            updateButtonStates();
        });

        mountSlider();
        return () => destroySlider();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [element, limit]);

    if (!array || array.length === 0) {
        return null;
    }

    const updateButtonStates = () => {
        if (!sliderRef.current) return;

        const { index } = sliderRef.current;
        const totalSlides = limitedArray.length;
        const perView = sliderRef.current.settings.perView;

        // ✅ Actualizar estado de los botones
        setIsAtStart(index === 0);
        setIsAtEnd(index + perView >= totalSlides);
    };

    const mountSlider = () => {
        try {
            sliderRef?.current?.mount();
        } catch (error) {
            console.log('faliled when mounting slider:', error);
            destroySlider();
        }
    };

    const destroySlider = () => {
        try {
            sliderRef?.current?.destroy();
        } catch (error) {
            console.log('faliled when destroying slider:', error);
        }
    };

    return (
        <Wrapper height={containerHeight} className={`${element}`}>
            <SliderWrapper className="glide__track" data-glide-el="track">
                <List className="glide__slides">
                    {limitedArray.map((item, index) => (
                        <Item
                            height={itemHeight}
                            key={index}
                            className="glide__slide"
                        >
                            <Card
                                handleOpenModal={() => {}}
                                value={item}
                                index={index}
                            />
                        </Item>
                    ))}
                </List>
            </SliderWrapper>
            <SliderTitle fontSize="20px" fontWeight="700" color="#B31D15">
                {title}
            </SliderTitle>
            <ControlsWrapper>
                {!!to && (
                    <Link to={to}>
                        <RedirectButton variant="text" color="tertiary">
                            Ver más
                        </RedirectButton>
                    </Link>
                )}
                { limitedArray.length > 3 && <ButtonsWrapper
                    className="glide__arrows"
                    data-glide-el="controls"
                >
                    <button
                        className="glide__arrow glide__arrow--left"
                        data-glide-dir="<"
                        disabled={isAtStart}
                    >
                        <StyledIconButton disabled={isAtStart}>
                            <ArrowBack />
                        </StyledIconButton>
                    </button>
                    <button
                        className="glide__arrow glide__arrow--right"
                        data-glide-dir=">"
                        disabled={isAtEnd}
                    >
                        <StyledIconButton disabled={isAtEnd}>
                            <ArrowForward />
                        </StyledIconButton>
                    </button>
                </ButtonsWrapper>}
            </ControlsWrapper>
        </Wrapper>
    );
};

export default Slider;

const Wrapper = styled.div`
    position: relative;
    display: flex;
    align-items: flex-end;
    height: ${({ height }) => height};
`;

const SliderWrapper = styled.div`
    height: fit-content;
    display: grid;
`;

const SliderTitle = styled(Text)`
    position: absolute;
    left: 0;
    top: 0;
`;

const ControlsWrapper = styled.div`
    display: flex;
    gap: 1rem;
    position: absolute;
    margin: auto;
    top: -10px;
    right: 0;
`;

const ButtonsWrapper = styled.div`
    display: flex;
    column-gap: 16px;

    svg {
        width: 20px;
        height: 20px;
        color: #616161;
    }
`;

const StyledIconButton = styled(IconButton)`
    &.Mui-disabled {
        opacity: 0.3;

        svg {
            color: #616161;
        }
    }

    svg {
        width: 20px;
        height: 20px;
        color: #616161;
    }
`;

const List = styled.ul`
    height: fit-content;
`;

const Item = styled.li`
    display: grid;
    place-items: center;
    height: ${({ height }) => height} !important;
    width: fit-content !important;
    padding: 0 0.2rem;
`;

const RedirectButton = styled(Button)`
    width: 120px;
    border-radius: var(--border-radius_rounded);
`;
